import React, { useEffect, useState } from 'react'
import { track } from '@adthlete/shared-frontend'
import { useDispatch } from 'react-redux'
import { useAuth } from '@/hooks/useAuth'
import { sendPhoneCodeThunk, verifyPhoneCodeThunk } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/components/ui/sonner'
import { INPUT_CLASSES } from '@/lib/theme'

/**
 * Shown by ProtectedRoute in place of the app until an athlete verifies a phone number.
 * Verifying updates Redux, so ProtectedRoute stops rendering this on its own.
 */
const PhoneGate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { athlete, logout } = useAuth()
  const [phone, setPhone] = useState(athlete?.phone || '')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Not a route of its own, so the sign-up funnel counts it by these two events
  useEffect(() => {
    track('phone_gate_shown')
  }, [])

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!phone || phone.length < 10) {
      toast.error('Invalid phone number', { description: 'Please enter a valid phone number.' })
      return
    }

    setIsLoading(true)
    try {
      await dispatch(sendPhoneCodeThunk(phone)).unwrap()
      setCodeSent(true)
      toast('Code sent', { description: `Verification code sent to ${phone}` })
    } catch (error: any) {
      toast.error('Error', { description: error || 'Failed to send verification code.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!code || code.length < 6) {
      toast.error('Invalid code', { description: 'Please enter a 6-digit code.' })
      return
    }

    setIsLoading(true)
    try {
      await dispatch(verifyPhoneCodeThunk({ phone, code })).unwrap()
      track('phone_verified')
      toast('Phone verified!', { description: 'Your phone number has been verified.' })
    } catch (error: any) {
      toast.error('Verification failed', { description: error || 'Invalid code.' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-background"></div>

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-semibold text-foreground">
              Verify your phone
            </CardTitle>
            <CardDescription>
              We need a phone number for your account. We'll text you a 6-digit code to confirm it.
              We will not share or sell your number.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!codeSent ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                <Input
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={INPUT_CLASSES.dark}
                  type="tel"
                  autoComplete="tel"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  variant="lime"
                  className="w-full"
                  disabled={isLoading || !phone}
                >
                  {isLoading ? 'Sending...' : 'Send Code'}
                </Button>
                <p className="text-xs text-gray-500 leading-relaxed">
                  By tapping <span className="text-gray-600">Send Code</span>, you agree to receive
                  a one-time SMS verification code from Athios. Standard messaging rates may apply.
                </p>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-4">
                <Input
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className={INPUT_CLASSES.dark}
                  maxLength={6}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  variant="lime"
                  className="w-full"
                  disabled={isLoading || !code}
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-gray-600"
                  onClick={() => setCodeSent(false)}
                  disabled={isLoading}
                >
                  Use a different number
                </Button>
              </form>
            )}
            <button
              type="button"
              onClick={logout}
              className="w-full text-center text-sm text-gray-600 hover:text-foreground transition-colors"
            >
              Sign out
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PhoneGate
